import { anim, prog, stagger } from '../../../../motion'
import { Fit } from '../../../../v2/kit'
import { useCopy } from '../../../i18n'
import codeknacker from '../../media/game-codeknacker.mp4'
import gefuehlschaosPoster from '../../media/game-gefuehlschaos.jpg'
import gefuehlschaos from '../../media/game-gefuehlschaos.mp4'
import hochstaplerPoster from '../../media/game-hochstapler.jpg'
import hochstapler from '../../media/game-hochstapler.mp4'
import selbsterkenntnisPoster from '../../media/game-selbsterkenntnis.jpg'
import selbsterkenntnis from '../../media/game-selbsterkenntnis.mp4'
import taetigkeitsvielfaltPoster from '../../media/game-taetigkeitsvielfalt.jpg'
import taetigkeitsvielfalt from '../../media/game-taetigkeitsvielfalt.mp4'
import { Odometer, ScrollVideo } from '../../stage/fx'

const CLIPS = [
  { src: codeknacker, color: '#43DBBD' },
  { src: hochstapler, poster: hochstaplerPoster, color: '#FE79A7' },
  { src: gefuehlschaos, poster: gefuehlschaosPoster, color: '#FFB628' },
  { src: selbsterkenntnis, poster: selbsterkenntnisPoster, color: '#8FB0FF' },
  {
    src: taetigkeitsvielfalt,
    poster: taetigkeitsvielfaltPoster,
    color: '#FE79A7'
  }
]

// Fünf Fenster, je eines pro Spiel
const START = 0.12
const SPAN = 0.16
const win = (i) => [START + i * SPAN, START + (i + 1) * SPAN]

export const TESTING_PARTICLES = [
  [
    0.02,
    (w, h, narrow) => ({
      type: 'orbit',
      x: w / 2,
      y: narrow ? h * 0.6 : h * 0.54,
      r: narrow ? w * 0.5 : h * 0.44
    })
  ],
  [
    0.96,
    (w, h, narrow) => ({
      type: 'orbit',
      x: w / 2,
      y: narrow ? h * 0.6 : h * 0.54,
      r: narrow ? w * 0.5 : h * 0.44,
      seed: 25
    })
  ]
]

const NARROW = { maxW: 0.62, maxH: 0.56 }

// Ein Smartphone mit den echten Spielen, dahinter rollt der Name des Spiels in riesiger Outline-Schrift
export default function DmsTesting() {
  const c = useCopy('testung')
  const s = useCopy('show')

  return (
    <div className='sc-full'>
      <div
        className='cin-split cin-split--left dms-split'
        style={anim({
          at: [0.01, 0.05],
          out: [0.09, 0.12],
          x: -80,
          y: 0,
          ox: -120,
          oy: 0
        })}
      >
        {c.split[0]}
      </div>
      <div
        className='cin-split cin-split--right dms-split'
        style={anim({
          at: [0.03, 0.07],
          out: [0.09, 0.12],
          x: 80,
          y: 0,
          ox: 120,
          oy: 0
        })}
      >
        {c.split[1]}
      </div>

      {s.games.map((g, i) => {
        const [a, b] = win(i)
        return (
          <div
            key={g.name}
            className='dms-bigname'
            style={{
              ...prog([a, b]),
              ...anim({ at: [a, a + 0.03], out: [b - 0.03, b], y: 0, oy: 0 })
            }}
            aria-hidden='true'
          >
            <span>{g.name}</span>
          </div>
        )
      })}

      <div
        className='sc-chip sc-real'
        style={anim({ at: [0.1, 0.14], y: -20 })}
      >
        <span className='sc-real__dot' />
        {s.realClips}
      </div>

      <Fit
        w={300}
        h={640}
        maxH={0.76}
        maxW={0.5}
        narrow={NARROW}
        className='sc-phone-fit'
      >
        <div
          className='sc-phone-3d'
          style={{
            ...prog([0, 0.12]),
            transform:
              'translateY(calc((1 - var(--p)) * 300px)) rotateX(calc((1 - var(--p)) * 50deg)) ' +
              'rotateY(calc((1 - var(--p)) * -30deg)) rotateZ(calc((1 - var(--p)) * 12deg))'
          }}
        >
          <div className='sc-phone'>
            <div className='sc-phone__screen'>
              {CLIPS.map((clip, i) => {
                const [a, b] = win(i)
                return (
                  <div
                    key={clip.src}
                    className='sc-clip'
                    style={anim({
                      at: i === 0 ? [-1, 0] : [a - 0.01, a + 0.01],
                      out:
                        i === CLIPS.length - 1
                          ? undefined
                          : [b - 0.01, b + 0.01],
                      y: 0,
                      oy: 0,
                      s: 1.06
                    })}
                  >
                    <ScrollVideo
                      src={clip.src}
                      poster={clip.poster}
                      at={[a - 0.02, b + 0.02]}
                      label={s.games[i].name}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </Fit>

      <div className='dms-gameinfo'>
        {s.games.map((g, i) => {
          const [a, b] = win(i)
          return (
            <div
              key={g.name}
              className='dms-gameinfo__card sc-glass'
              style={{
                '--tone': CLIPS[i].color,
                ...anim({
                  at: [a + 0.01, a + 0.04],
                  out: i === s.games.length - 1 ? undefined : [b - 0.03, b],
                  x: -40,
                  y: 0,
                  ox: -40,
                  oy: 0
                })
              }}
            >
              <div className='dms-gameinfo__group'>{g.group}</div>
              <div className='dms-gameinfo__name'>{g.name}</div>
              <div className='dms-gameinfo__meta'>
                <span>{g.dim}</span>
                <span>{g.time}</span>
              </div>
            </div>
          )
        })}
      </div>

      <div className='dms-gamestats'>
        {s.gameStats.map((st, i) => (
          <div
            key={st.label}
            className='dms-gamestat sc-glass'
            style={anim({ at: stagger(0.22, 0.22, 0.05, i), x: 60, y: 0 })}
          >
            <Odometer
              value={st.value}
              at={stagger(0.23, 0.22, 0.08, i)}
              className='sc-num dms-gamestat__num'
            />
            <div className='dms-gamestat__label'>{st.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
